import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Placeholder from "@/v2-ui/Placeholder"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import { useLayerQuery } from "@/v2-backoffice-client/layer/layer.query"
import LayerUptimeCell from "@/v2-backoffice-client/layer/LayerUptimeCell"
import { LayerUptimeMapProvider } from "@/v2-backoffice-client/layer/LayerUptimeMapContext"
import LayerDriftCell from "@/v2-backoffice-client/layer/drift/LayerDriftCell"
import { LayerDriftMapProvider } from "@/v2-backoffice-client/layer/drift/LayerDriftMapContext"
import type { LayerFragment } from "@/v2-backoffice-client/layer/__types__/layer.fragment"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import LayerReviewPublishButton from "@/v2-backoffice-client/layer/review/LayerReviewPublishButton"
import LayerReviewRequeueButton from "@/v2-backoffice-client/layer/review/LayerReviewRequeueButton"
import LayerConsolePreviewButton from "@/v2-backoffice-client/layer/review/LayerConsolePreviewButton"
import { AGOL_SEARCH_BASE_URL } from "@/v2-backoffice-client/layer/layer.constants"
import {
  LAYER_SEARCH_COLUMN,
  LAYER_SEARCH_COLUMN_ID,
  LAYER_SEARCH_PLACEHOLDER,
} from "@/v2-backoffice-client/layer/layer.columns"

const COLUMN_SIZING = {
  ReviewedAt: 130,
  Uptime: 200,
  Drift: 120,
  Review: 340,
}

const columns = createColumns<LayerFragment>({
  enableSelection: true,
  enableSorting: true,
  columns: {
    Name: {
      id: "Name",
      label: "Name",
      to: ({ row }) => `?v=layer:${row.original.FeatureCode}`,
    },
    FeatureCode: {
      id: "FeatureCode",
      label: "Feature Code",
      filter: { type: "text", placeholder: "Filter by code..." },
      to: ({ value }) => `${AGOL_SEARCH_BASE_URL}${value}`,
    },
    [LAYER_SEARCH_COLUMN_ID]: LAYER_SEARCH_COLUMN,
    ReviewedAt: {
      id: "ReviewedAt",
      label: "Rejected",
      renderCell({ row }) {
        if (!row.original.ReviewedAt) return null
        return formatDateString(String(row.original.ReviewedAt))
      },
    },
    ReviewedByName: {
      id: "ReviewedByName",
      label: "Rejected by",
    },
    // Uptime + Drift are joined client-side via the shared lookup
    // providers (see LayerTable) so an operator sees upstream health
    // before publishing a rejected layer after all.
    Uptime: {
      id: "Uptime",
      label: "Uptime",
      enableSorting: false,
      renderCell({ row }) {
        return <LayerUptimeCell featureCode={row.original.FeatureCode} />
      },
    },
    Drift: {
      id: "Drift",
      label: "Drift",
      enableSorting: false,
      renderCell({ row }) {
        return <LayerDriftCell featureCode={row.original.FeatureCode} />
      },
    },
    ReviewNote: {
      id: "ReviewNote",
      label: "Note",
      renderCell({ row }) {
        const note = row.original.ReviewNote
        if (!note) return null
        return (
          <span className="truncate max-w-[240px]" title={note}>
            {note}
          </span>
        )
      },
    },
    Review: {
      id: "Review",
      label: "Actions",
      // Action buttons only; there's no row value to sort on.
      enableSorting: false,
      renderCell({ row }) {
        return (
          <span className="layer-rejected-table-actions flex items-center gap-2">
            <LayerConsolePreviewButton layer={row.original} />
            <LayerReviewPublishButton layers={[row.original]} />
            <LayerReviewRequeueButton layers={[row.original]} />
          </span>
        )
      },
    },
  },
})

export type LayerRejectedTableProps = Omit<
  TableProps<LayerFragment>,
  "data" | "columns"
>

/**
 * Rejected layers: reviewed in backoffice but left disabled, so they stay
 * hidden from customers. From here a layer can be published after all, or
 * moved back to the review queue (un-rejected) for another look.
 */
function LayerRejectedTable(props: LayerRejectedTableProps) {
  const { className, ...tableProps } = props

  const opener = useAppViewSheetOpener()

  const { data, previousData, loading, error, refetch } = useLayerQuery({
    notifyOnNetworkStatusChange: true,
    variables: { input: { isReviewed: true, isEnabled: false } },
  })

  const displayData = data ?? previousData
  // Also filter client-side so a just-published or just-requeued layer drops
  // out immediately from the normalized cache update, without a refetch.
  const layers = (displayData?.getLayers ?? []).filter(
    (layer) => layer.ReviewedAt && !layer.IsEnabled,
  )

  return (
    <LayerUptimeMapProvider>
      <LayerDriftMapProvider>
        <Table
          className={cn("layer-rejected-table", className)}
          tableId="layer-rejected-table"
          data={layers}
          columns={columns}
          initialColumnSizing={COLUMN_SIZING}
          enableRowSelection
          getHighlightedRowId={(row) => {
            return opener.getIsActive("layer", String(row.original.FeatureCode))
          }}
          {...tableProps}
        >
          <Table.Toolbar>
            <p className="text-sm text-muted-foreground">
              Rejected layers stay hidden from customers. Take one live after
              all, or move it back to the review queue for another look.
            </p>

            <div className="flex-1" />

            <Table.Search
              column={LAYER_SEARCH_COLUMN_ID}
              placeholder={LAYER_SEARCH_PLACEHOLDER}
            />
            <Separator orientation="vertical" />
            <Table.ToolbarMenu
              onRefresh={refetch}
              exportFileName="layers-rejected"
            />
          </Table.Toolbar>

          <Table.FilterList />

          <Table.Content
            isLoading={loading}
            error={error}
            renderEmpty={<Placeholder>No rejected layers.</Placeholder>}
          />

          <Table.SelectionToolbar<LayerFragment>>
            {(selectedLayers, resetSelection) => (
              <>
                <LayerReviewPublishButton
                  variant="ghost"
                  isStacked
                  layers={selectedLayers}
                  onDone={resetSelection}
                />
                <LayerReviewRequeueButton
                  variant="ghost"
                  isStacked
                  layers={selectedLayers}
                  onDone={resetSelection}
                />
              </>
            )}
          </Table.SelectionToolbar>
        </Table>
      </LayerDriftMapProvider>
    </LayerUptimeMapProvider>
  )
}

export default LayerRejectedTable
